import * as fabric from 'fabric'
import { convertFontSize, generateUniqueId, normalizeHexColor } from './index'
import {
  getColorDisplayValue,
  getNormalizedValueForVariant,
  normalizeVectorColorsToPantone,
  normalizeVectorColorsToSingleColor,
} from './fabric-editor-colors'
import { EditorColorType, EditorColoreApiType, EditorImageType, EditorTextType } from '@utils/types'

/**
 * Logiche di caricamento oggetti (immagini/testi) estratte da `useFabricEditor`.
 */
// Tolleranza di fit in px rispetto alla print area:
// - 0 => oggetto al massimo uguale alla print area
// - negativa => imponiamo un piccolo margine interno per assorbire errori floating-point
//   (es. 1980.8600000000008 vs 1980.86) evitando overflow "tecnici".
const FIT_TOLERANCE = -0.0001
const MAX_HARD_FIT_ITERATIONS = 20

const nextDown = (value: number) => {
  if (!Number.isFinite(value)) return value
  if (value === 0) return -Number.MIN_VALUE

  const buffer = new ArrayBuffer(8)
  const view = new DataView(buffer)
  view.setFloat64(0, value)

  const high = view.getUint32(0)
  const low = view.getUint32(4)

  if (value > 0) {
    if (low === 0) {
      view.setUint32(0, high - 1)
      view.setUint32(4, 0xffffffff)
    } else {
      view.setUint32(4, low - 1)
    }
  } else if (low === 0xffffffff) {
    view.setUint32(0, high + 1)
    view.setUint32(4, 0)
  } else {
    view.setUint32(4, low + 1)
  }

  return view.getFloat64(0)
}

// Dopo `scaleToWidth/scaleToHeight` Fabric puo' lasciare micro-sforamenti dovuti alla
// precisione floating-point. Questo clamp forza l'oggetto a rientrare nella print area
// gia' in fase di load, evitando che risulti "fuori area" ai controlli di containment.
const clampObjectToArea = (obj: fabric.Object, area: fabric.Object) => {
  const areaWidth = area.getScaledWidth()
  const areaHeight = area.getScaledHeight()

  if (
    !Number.isFinite(areaWidth) ||
    !Number.isFinite(areaHeight) ||
    areaWidth <= 0 ||
    areaHeight <= 0
  ) {
    return
  }

  for (let pass = 0; pass < MAX_HARD_FIT_ITERATIONS; pass += 1) {
    const objectWidth = obj.getScaledWidth()
    const objectHeight = obj.getScaledHeight()

    if (
      !Number.isFinite(objectWidth) ||
      !Number.isFinite(objectHeight) ||
      objectWidth <= 0 ||
      objectHeight <= 0
    ) {
      return
    }

    const overflowWidth = objectWidth - areaWidth
    const overflowHeight = objectHeight - areaHeight

    // Stop quando l'overflow e' sotto soglia su entrambi gli assi.
    // Con FIT_TOLERANCE negativa, "sotto soglia" significa stare leggermente dentro.
    if (overflowWidth <= FIT_TOLERANCE && overflowHeight <= FIT_TOLERANCE) {
      return
    }

    // Shrink uniforme: preserva il rapporto originale dell'immagine/SVG.
    const shrinkRatio = Math.min(areaWidth / objectWidth, areaHeight / objectHeight)
    const currentScaleX = Number.isFinite(obj.scaleX) ? obj.scaleX || 1 : 1
    const currentScaleY = Number.isFinite(obj.scaleY) ? obj.scaleY || 1 : 1

    obj.set({
      // `nextDown` riduce di 1 ULP il risultato per evitare che, dopo i calcoli in double,
      // resti un micro-overflow anche quando il ratio e' teoricamente perfetto.
      scaleX: nextDown(currentScaleX * shrinkRatio),
      scaleY: nextDown(currentScaleY * shrinkRatio),
    })
    obj.setCoords()
  }
}

export const loadSvgToCanvas = ({
  canvas,
  areaStampa,
  preview,
  preferredColorVariant,
  effectiveGlobalColorHex,
  pantoneReference = [],
  objects,
  options,
  id,
  meta = {},
}: {
  canvas: fabric.Canvas | null | undefined
  areaStampa: fabric.Object | null | undefined
  preview: boolean
  preferredColorVariant: EditorColorType['variant']
  effectiveGlobalColorHex: string
  pantoneReference?: EditorColoreApiType[]
  objects: any
  options: any
  id: number | string
  meta?: any
}) => {
  if (!canvas || !areaStampa) return

  const svgObject = fabric.util.groupSVGElements(objects, options)

  svgObject.set({
    left: areaStampa.left,
    top: areaStampa.top,
    angle: areaStampa.angle,
  })

  if (
    svgObject.width / svgObject.height >
    areaStampa.getScaledWidth() / areaStampa.getScaledHeight()
  ) {
    svgObject.scaleToWidth(areaStampa.getScaledWidth())
  } else {
    svgObject.scaleToHeight(areaStampa.getScaledHeight())
  }
  clampObjectToArea(svgObject, areaStampa)

  svgObject.setCoords()
  ;(svgObject as any).customId = id
  ;(svgObject as any).editorObjectControl = {
    ...((svgObject as any)?.editorObjectControl || {}),
    editorMeta: meta,
  }

  if ((meta as any)?.is_vector) {
    if (preferredColorVariant === 'pantone') {
      normalizeVectorColorsToPantone(svgObject, effectiveGlobalColorHex, pantoneReference)
    } else {
      normalizeVectorColorsToSingleColor(svgObject, effectiveGlobalColorHex)
    }
  }

  canvas.add(svgObject)
  if (!preview) canvas.setActiveObject(svgObject)
  canvas.renderAll()
}

export const loadSvgFromUrl = ({
  url,
  id,
  meta = {},
  onLoaded,
}: {
  url: string
  id: number
  meta?: any
  onLoaded: (objects: any, options: any, id: number, meta?: any) => void
}) => {
  return fabric
    .loadSVGFromURL(
      `${process.env.NODE_ENV === 'development' ? 'http://localhost:9999' : ''}${url}`
    )
    .then(({ objects, options }) => {
      onLoaded(objects, options, id, meta)
    })
}

export const loadImageObject = ({
  image,
  onLoadSvgFromUrl,
}: {
  image: EditorImageType
  onLoadSvgFromUrl: (url: string, id: number, meta?: any) => Promise<void>
}) => {
  return onLoadSvgFromUrl(image.image, image.id, image)
}

export const loadTextObject = ({
  canvas,
  areaStampa,
  preview,
  text,
  preferredColorVariant,
  effectiveGlobalColorHex,
  pantoneReference = [],
}: {
  canvas: fabric.Canvas | null | undefined
  areaStampa: fabric.Object | null | undefined
  preview: boolean
  text: EditorTextType
  preferredColorVariant: EditorColorType['variant']
  effectiveGlobalColorHex: string
  pantoneReference?: EditorColoreApiType[]
}) => {
  if (!canvas || !areaStampa) return

  const defaultColor =
    getNormalizedValueForVariant(
      effectiveGlobalColorHex || '#ee1515',
      undefined,
      preferredColorVariant,
      pantoneReference
    ) || '#ee1515'

  if (!text.colore) {
    text.colore = {
      id: defaultColor,
      value: defaultColor,
      variant: preferredColorVariant,
      displayValue: getColorDisplayValue(defaultColor, preferredColorVariant, pantoneReference),
      originalValue: defaultColor,
    } as EditorColorType
  } else {
    const normalizedGlobalColor = effectiveGlobalColorHex
      ? getNormalizedValueForVariant(
          effectiveGlobalColorHex,
          undefined,
          preferredColorVariant,
          pantoneReference
        )
      : ''
    const baseTextColor = text.colore.value || text.colore.originalValue || text.colore.id || ''
    const normalizedBaseTextColor = getNormalizedValueForVariant(
      baseTextColor,
      text.colore.variant,
      preferredColorVariant,
      pantoneReference
    )
    const normalizedTextColor = normalizedGlobalColor || normalizedBaseTextColor || defaultColor
    const normalizedOriginalValue =
      normalizedGlobalColor ||
      getNormalizedValueForVariant(
        text.colore.originalValue ||
          text.colore.id ||
          normalizedBaseTextColor ||
          normalizedTextColor,
        text.colore.variant,
        preferredColorVariant,
        pantoneReference
      ) ||
      normalizedTextColor

    text.colore = {
      ...text.colore,
      id: normalizeHexColor(normalizedTextColor),
      value: normalizedTextColor,
      originalValue: normalizedOriginalValue,
      variant: preferredColorVariant,
      displayValue: getColorDisplayValue(
        normalizedTextColor,
        preferredColorVariant,
        pantoneReference
      ),
    }
  }

  const textObj = new fabric.FabricText(text.text, {
    fontFamily: text.fontFamily || 'Arial',
    fontSize: text.fontSize || convertFontSize(24),
    fill: text.colore?.value || defaultColor,
    fontWeight: text.fontWeight || '400',
    fontStyle: text.fontStyle || 'normal',
    underline: text.underline || false,
  })

  textObj.set({
    left: areaStampa.left,
    top: areaStampa.top,
    angle: areaStampa.angle,
  })

  textObj.initDimensions()
  textObj.setCoords()

  const maxWidth = areaStampa.getScaledWidth() - 20
  const maxHeight = areaStampa.getScaledHeight() - 20

  const scaleW = maxWidth / textObj.width
  const scaleH = maxHeight / textObj.height
  const finalScale = Math.min(scaleW, scaleH)
  textObj.set({
    fontSize: Math.floor(textObj.fontSize * finalScale),
    scaleX: 1,
    scaleY: 1,
  })

  textObj.setCoords()
  const textId = text.id || generateUniqueId()
  ;(textObj as any).customId = textId
  ;(textObj as any).editorObjectControl = {
    ...((textObj as any)?.editorObjectControl || {}),
    editorMeta: { ...text },
  }

  canvas.add(textObj)
  if (!preview) canvas.setActiveObject(textObj)
  canvas.renderAll()
}
